// react
import { useState, useRef, useEffect, useMemo } from "react";
import { PillsInput, Pill, Combobox, useCombobox, LoadingOverlay } from "@mantine/core";
import { Search } from "lucide-react";
import { useDispatch } from 'react-redux';
import { setCurrentGame, setSearchQuery } from '@/store/features/gameSlice';
import { useRouter } from 'next/router';

// scss
import styles from "./search.module.scss";

// utils
import debounce from "@/utils/methods";
import { getAllGames } from "@/api/header";
import { gameClicked } from "@/utils/event";

export default function SearchInput() {
    const [gameList, setGameList] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const abortControllerRef = useRef(null);
    const dispatch = useDispatch();
    const searchIcon = <Search size={16} onClick={() => dispatch(setSearchQuery(searchInput))} />;
    const router = useRouter();
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });

    const filterGameList = useMemo(() => {
        const searchInputTrim = searchInput.trim();

        return !searchInputTrim ? [] : gameList?.reduce((arr, item) => {
            if (item.name && item.name.toLowerCase().includes(searchInputTrim.toLowerCase())) {
                arr.push({
                    value: item.path,
                    label: item.name || item.path,
                    game: item,
                });
            }
            return arr;
        }, []);
    }, [searchInput]);

    let [params] = useState({
        limit: 200,
        offset: 0,
        order: 'desc',
        sortby: 'create_at',
    });

    const fetchGames = async () => {
        // TODO：后端开发好后这里要替换一下接口传参
        // let params = { query: searchInput };

        try {
            // 取消之前的请求
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // 创建新的AbortController
            abortControllerRef.current = new AbortController();

            setFetchLoading(true);
            const res = await getAllGames(params, abortControllerRef.current);
            let { results } = res.data;
            results = results ? results : [];
            setGameList(results);
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Request was aborted');
            } else {
                console.error('Error fetching games:', error);
            }
        } finally {
            setFetchLoading(false);
        }
    };

    // TODO: 正式上线这里要移除
    // 初始加载时获取所有游戏
    useEffect(() => {
        fetchGames();
        // 组件卸载时取消未完成的请求
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const onSearchChange = (value) => {
        if (!value) {
            setSearchInput("");
            return;
        }
        setSearchInput(value);
        searchGame(value);
    };

    const searchGame = debounce(async (path) => {
        // TODO: 正式上线这里要取消注释
        // fetchGames();
    }, 300);

    const selectGame = (gamePath, option) => {
        const selectedGame = gameList.find(game => game.path === gamePath);
        if (selectedGame) {
            selectedGame.clicked_count++;
            gameClicked({ path: gamePath });
            dispatch(setCurrentGame(selectedGame));
            router.push(`/game/${gamePath}`);
            combobox.closeDropdown();
        }
    };

    const options = filterGameList.map((item) => (
        <Combobox.Option value={item.value} key={item.value}>
            {item.label}
        </Combobox.Option>
    ));

    return (
        <Combobox
            store={combobox}
            onOptionSubmit={selectGame}
        >
            <Combobox.Target>
                <PillsInput
                    className={styles.search}
                    rightSection={searchIcon}
                    onClick={() => combobox.openDropdown()}
                    radius={24}
                    size="sm"
                >
                    <Pill.Group>
                        <PillsInput.Field
                            value={searchInput}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search Game By Name"
                            onFocus={() => combobox.openDropdown()}
                            onBlur={() => combobox.closeDropdown()}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    dispatch(setSearchQuery(searchInput));
                                }
                            }}
                        />
                    </Pill.Group>
                </PillsInput>
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options className={styles.combobox_options}>
                    {fetchLoading ? <LoadingOverlay
                        visible={true}
                        zIndex={2000}
                        overlayProps={{ radius: 'sm', opacity: 0.1 }}
                        loaderProps={{ color: 'lightgrey', type: 'bars' }}
                    /> : options.length > 0 ? options : (
                        <Combobox.Empty>Nothing found...</Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
}